/**
 * ThreatRadar — Radar / spider chart showing vulnerability distribution
 * by category, wrapped in a glass-card with an accent border glow.
 *
 * Uses Recharts RadarChart under the hood, styled to match the VulnZero
 * dark cyber aesthetic.
 *
 * Props:
 *   data       Array<{ category, count, maxCount }>
 *   className  string
 *
 * Default categories (used when no data supplied):
 *   Injection, XSS, RCE, Auth Bypass, Config, Crypto, SSRF, DoS
 */
import { useMemo } from 'react'
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Tooltip,
} from 'recharts'
import { motion } from 'framer-motion'
import clsx from 'clsx'

/* ================================================================
   DESIGN TOKENS
   ================================================================ */
const ACCENT = '#00c8ff'
const ACCENT_DIM = '#0090b8'
const ACCENT_FILL = 'rgba(0, 200, 255, 0.15)'
const SURFACE_0 = '#07080f'
const SURFACE_2 = '#111425'
const SURFACE_3 = '#171b30'
const EDGE = '#1e2240'
const EDGE_SUBTLE = '#161935'
const EDGE_ACCENT = 'rgba(0, 200, 255, 0.18)'
const TEXT_PRIMARY = '#e8eaf0'
const TEXT_SECONDARY = '#8b90a8'
const TEXT_TERTIARY = '#5a5f78'
const ACCENT_GHOST = 'rgba(0, 200, 255, 0.08)'

const DEFAULT_CATEGORIES = [
  'Injection',
  'XSS',
  'RCE',
  'Auth Bypass',
  'Config',
  'Crypto',
  'SSRF',
  'DoS',
]

/* ================================================================
   CUSTOM TOOLTIP
   ================================================================ */
function RadarTooltip({ active, payload }) {
  if (!active || !payload || !payload.length) return null
  const { category, count, maxCount } = payload[0].payload

  const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0

  return (
    <div
      className="rounded-lg border backdrop-blur-md px-3.5 py-2.5"
      style={{
        background: 'rgba(7, 8, 15, 0.92)',
        borderColor: ACCENT + '35',
        boxShadow: `0 0 20px ${ACCENT}15, 0 4px 16px rgba(0,0,0,0.5)`,
        minWidth: 140,
      }}
    >
      <p
        className="text-xs font-semibold mb-1"
        style={{ color: TEXT_PRIMARY }}
      >
        {category}
      </p>
      <div className="flex items-baseline gap-2">
        <span
          className="text-lg font-bold tabular-nums"
          style={{ color: ACCENT }}
        >
          {count}
        </span>
        <span className="text-[10px]" style={{ color: TEXT_TERTIARY }}>
          / {maxCount}
        </span>
      </div>
      {/* Mini progress bar */}
      <div
        className="mt-1.5 h-1 rounded-full overflow-hidden"
        style={{ background: EDGE }}
      >
        <div
          className="h-full rounded-full"
          style={{
            width: `${pct}%`,
            background: `linear-gradient(90deg, ${ACCENT_DIM}, ${ACCENT})`,
            transition: 'width 0.3s ease',
          }}
        />
      </div>
    </div>
  )
}

/* ================================================================
   CUSTOM AXIS TICK
   ================================================================ */
function AxisTick({ payload, x, y, cx, cy }) {
  // Push label outward slightly from center
  const dx = x - cx
  const dy = y - cy
  const dist = Math.sqrt(dx * dx + dy * dy)
  const nudge = 12
  const nx = x + (dx / dist) * nudge
  const ny = y + (dy / dist) * nudge

  return (
    <text
      x={nx}
      y={ny}
      textAnchor="middle"
      dominantBaseline="central"
      fill={TEXT_SECONDARY}
      fontSize="10"
      fontFamily="'DM Sans', sans-serif"
      fontWeight="500"
    >
      {payload.value}
    </text>
  )
}

/* ================================================================
   CUSTOM DOT
   ================================================================ */
function RadarDot({ cx, cy }) {
  return (
    <g>
      {/* Outer glow ring */}
      <circle cx={cx} cy={cy} r={5} fill={ACCENT + '20'} stroke="none" />
      {/* Solid dot */}
      <circle
        cx={cx}
        cy={cy}
        r={3}
        fill={ACCENT}
        stroke={SURFACE_2}
        strokeWidth={1.5}
        style={{ filter: `drop-shadow(0 0 3px ${ACCENT})` }}
      />
    </g>
  )
}

/* ================================================================
   MAIN COMPONENT
   ================================================================ */
export default function ThreatRadar({ data, className }) {
  // Normalise data — fill missing categories with zeroes
  const chartData = useMemo(() => {
    if (data && data.length > 0) return data

    return DEFAULT_CATEGORIES.map(cat => ({
      category: cat,
      count: 0,
      maxCount: 100,
    }))
  }, [data])

  const globalMax = useMemo(
    () => Math.max(...chartData.map(d => d.maxCount), 1),
    [chartData],
  )

  const totalCount = useMemo(
    () => chartData.reduce((sum, d) => sum + d.count, 0),
    [chartData],
  )

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96, y: 12 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={clsx(
        'relative rounded-2xl border overflow-hidden',
        className,
      )}
      style={{
        background: SURFACE_2,
        borderColor: EDGE,
        boxShadow: `
          0 1px 2px rgba(0,0,0,0.3),
          0 4px 16px rgba(0,0,0,0.2),
          inset 0 0 0 1px ${EDGE_ACCENT}
        `,
      }}
    >
      {/* Subtle corner glow */}
      <div
        className="absolute -top-20 -right-20 w-48 h-48 rounded-full pointer-events-none"
        style={{
          background: `radial-gradient(circle, ${ACCENT}08 0%, transparent 70%)`,
        }}
      />

      {/* Header */}
      <div className="px-5 pt-4 pb-2 flex items-center justify-between">
        <div>
          <h3
            className="text-sm font-semibold"
            style={{
              color: TEXT_PRIMARY,
              fontFamily: "'Instrument Sans', 'DM Sans', sans-serif",
            }}
          >
            Threat Radar
          </h3>
          <p className="text-[10px] mt-0.5" style={{ color: TEXT_TERTIARY }}>
            Vulnerability distribution by category
          </p>
        </div>
        {/* Total badge */}
        <div
          className="flex items-center gap-1.5 rounded-md px-2 py-0.5"
          style={{
            background: ACCENT_GHOST,
            border: `1px solid ${ACCENT}20`,
          }}
        >
          <span
            className="w-1.5 h-1.5 rounded-full"
            style={{ background: ACCENT, boxShadow: `0 0 4px ${ACCENT}` }}
          />
          <span
            className="text-[10px] font-bold tabular-nums"
            style={{ color: ACCENT }}
          >
            {totalCount}
          </span>
          <span className="text-[10px]" style={{ color: TEXT_TERTIARY }}>
            total
          </span>
        </div>
      </div>

      {/* Chart */}
      <div className="px-2 pb-4" style={{ height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart
            cx="50%"
            cy="50%"
            outerRadius="72%"
            data={chartData}
          >
            {/* Grid */}
            <PolarGrid
              stroke={EDGE_SUBTLE}
              strokeDasharray="2 3"
              strokeOpacity={0.6}
            />

            {/* Axis labels */}
            <PolarAngleAxis
              dataKey="category"
              tick={<AxisTick />}
              stroke={EDGE_SUBTLE}
              strokeOpacity={0.3}
            />

            {/* Radius axis (hidden labels, just grid rings) */}
            <PolarRadiusAxis
              angle={90}
              domain={[0, globalMax]}
              tick={false}
              axisLine={false}
              stroke={EDGE_SUBTLE}
              strokeOpacity={0.2}
            />

            {/* Data area */}
            <Radar
              name="Vulnerabilities"
              dataKey="count"
              stroke={ACCENT}
              strokeWidth={1.8}
              fill={ACCENT_FILL}
              fillOpacity={1}
              dot={<RadarDot />}
              animationBegin={200}
              animationDuration={1200}
              animationEasing="ease-out"
              isAnimationActive={true}
            />

            {/* Tooltip */}
            <Tooltip
              content={<RadarTooltip />}
              cursor={false}
              wrapperStyle={{ outline: 'none' }}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Bottom stats strip */}
      <div
        className="px-5 py-2.5 flex items-center justify-between border-t"
        style={{ borderColor: EDGE_SUBTLE }}
      >
        {chartData.slice(0, 4).map(d => {
          const pct = d.maxCount > 0 ? Math.round((d.count / d.maxCount) * 100) : 0
          return (
            <div key={d.category} className="flex flex-col items-center gap-0.5">
              <span
                className="text-[10px] font-medium"
                style={{ color: TEXT_TERTIARY }}
              >
                {d.category}
              </span>
              <span
                className="text-xs font-bold tabular-nums"
                style={{ color: pct > 60 ? '#ff2d55' : pct > 30 ? '#ffb020' : ACCENT }}
              >
                {pct}%
              </span>
            </div>
          )
        })}
      </div>
    </motion.div>
  )
}
